import React, { useContext, useMemo } from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { Checkbox, Form, Grid, Input, Message } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { LandscapeFeatureType, SpecialEcologicalArea } from '~/api/services/climaCheck'
import { StyledGroup } from '../../../../Co2Climate/AnswerCO2Climate/Steps/GrassSilageStep/style'
import { StyledDivider } from '../ElevenStep/style'
import { SummaryOptionsContext } from '.'
import { formatNumber } from '~/utils/helpers'
import InfoPopup from '~/components/InfoPopup'

const VALID_SUM_MIN = 1
const VALID_SUM_MAX = 20000

function computeArraySum(values: any[] | undefined): number {
  if (!values || !Array.isArray(values)) return 0
  return values.reduce((sum, item) => {
    const val = typeof item?.value === 'string' ? parseFloat(item.value.replace(',', '.')) : Number(item?.value)
    return sum + (isNaN(val) ? 0 : val)
  }, 0)
}

const OekologieStep: React.FC = () => {
  const { control, setValue, clearErrors, setError } = useFormContext()
  const { landscapeFeatureTypes, specialEcologicalAreas } = useContext(SummaryOptionsContext)

  const watchLandscapeFeaturesPresent = useWatch({ control, name: 'additionalQuestions.landscapeFeaturesPresent' })
  const watchOtherAreasPresent = useWatch({ control, name: 'additionalQuestions.otherAreasWithSpecialEcologicalValuePresent' })

  const watchLandscapeValues = useWatch({ control, name: 'additionalQuestions.landscapeFeatureTypes' })
  const watchEcologicalValues = useWatch({ control, name: 'additionalQuestions.specialEcologicalAreas' })

  const { fields: landscapeFields, append: appendLandscape, remove: removeLandscape } = useFieldArray({
    control,
    name: 'additionalQuestions.landscapeFeatureTypes',
    keyName: 'key'
  })

  const { fields: ecologicalFields, append: appendEcological, remove: removeEcological } = useFieldArray({
    control,
    name: 'additionalQuestions.specialEcologicalAreas',
    keyName: 'key'
  })

  const landscapeSum = useMemo(() => computeArraySum(watchLandscapeValues), [watchLandscapeValues])
  const ecologicalSum = useMemo(() => computeArraySum(watchEcologicalValues), [watchEcologicalValues])
  const totalSum = landscapeSum + ecologicalSum

  const hasAnyValues = watchLandscapeValues?.some(item => item?.value !== '' && item?.value !== undefined)
    || watchEcologicalValues?.some(item => item?.value !== '' && item?.value !== undefined)

  const isTotalSumInvalid = hasAnyValues && (totalSum < VALID_SUM_MIN || totalSum > VALID_SUM_MAX)

  function handleLandscapeChecked(feature: LandscapeFeatureType, checked: boolean) {
    if (checked) {
      appendLandscape({ id: feature.id, value: '' })
    } else {
      const index = landscapeFields.findIndex(f => f['id'] === feature.id)
      if (index >= 0) {
        removeLandscape(index)
      }
    }
  }

  function handleEcologicalChecked(area: SpecialEcologicalArea, checked: boolean) {
    if (checked) {
      appendEcological({ id: area.id, value: '' })
    } else {
      const index = ecologicalFields.findIndex(f => f['id'] === area.id)
      if (index >= 0) {
        removeEcological(index)
      }
    }
  }

  return (
    <div style={{ marginTop: '2em' }}>

      <StyledGroup widths={'equal'}>
        <FormFields.Input
          control={control}
          rules={{ required: true }}
          withoutAsterisk
          noErrorMessage
          typeInput={'controled'}
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{'Wie viel landwirtschaftliche Fläche bewirtschaften Sie insgesamt?'}</label>
            <InfoPopup
              content={`Dabei handelt es sich um Flächen, die durch ihre extensive Bewirtschaftung, i
              hre Naturnähe und ihre vielfältigen Strukturen eine deutlich höhere biologische Vielfalt aufweisen als gewöhnlich intensiv genutzte Agrarflächen. 
              Dazu zählen vor allem artenreiche Grünland-, Acker-, Brach- und Weinbergsbereiche sowie Streuobstwiesen und typische Strukturelemente der Kulturlandschaft.`}
              styleIcon={{ fontSize: '1.2em' }}
              position={'bottom center'}
            />
          </div>}
          suffix=' ha'
          placeholder={'in ha'}
          name={'additionalQuestions.totalManagedAgriculturalLand'}
        />
      </StyledGroup>

      <StyledGroup widths={'equal'}>
        <Controller
          control={control}
          name='additionalQuestions.landscapeFeaturesPresent'
          rules={{
            required: true
          }}
          render={({ fieldState: { error } }) => (
            <Form.Field error={Boolean(error)}>
              <label className='title' style={{ width: 'fit-content' }}>
                <div>{'Sind Landschaftselemente auf Ihren Flächen vorhanden?'}</div>
                <StyledDivider />
              </label>
              <div style={{ display: 'flex', gap: 20 }}>
                <Checkbox
                  radio
                  label={'Vorhanden'}
                  checked={watchLandscapeFeaturesPresent === 1}
                  onClick={() => {
                    setValue('additionalQuestions.landscapeFeaturesPresent', 1)
                    clearErrors('additionalQuestions.landscapeFeaturesPresent')
                  }}
                />
                <Checkbox
                  radio
                  label={'Nicht vorhanden'}
                  checked={watchLandscapeFeaturesPresent === 0}
                  onClick={() => {
                    setValue('additionalQuestions.landscapeFeaturesPresent', 0)
                    setValue('additionalQuestions.landscapeFeatureTypes', [])
                    setValue('additionalQuestions.otherAreasWithSpecialEcologicalValuePresent', 0)
                    setValue('additionalQuestions.specialEcologicalAreas', [])
                    clearErrors('additionalQuestions.landscapeFeaturesPresent')
                  }}
                />
              </div>
            </Form.Field>
          )}
        />
      </StyledGroup>

      {Boolean(watchLandscapeFeaturesPresent) && (
        <StyledGroup widths={'equal'}>
          <Form.Field>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Welche Arten von Landschaftselementen sind vorhanden?'}</div>
              <StyledDivider />
            </label>
            <Grid columns={2} style={{ marginTop: '1em' }}>
              {landscapeFeatureTypes.map(feature => {
                const fieldIndex = landscapeFields.findIndex(f => f['id'] === feature.id)
                const isChecked = fieldIndex >= 0
                const fieldValue = isChecked ? landscapeFields[fieldIndex]['value'] : ''
                const displayLabel = isChecked && fieldValue
                  ? `${feature.label} (${fieldValue} ${feature.unitLabel})`
                  : feature.label

                return (
                  <Grid.Row key={feature.id} style={{ alignItems: 'center', padding: '0.6rem 0rem' }}>
                    <Grid.Column width={8}>
                      <Checkbox
                        label={displayLabel}
                        checked={isChecked}
                        onChange={(_, data) => handleLandscapeChecked(feature, data.checked)}
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      {isChecked ? (
                        <FormFields.Input
                          control={control}
                          allowDecimals
                          decimalScale={1}
                          style={{ width: 200 }}
                          styleLabel={{ marginBottom: 0 }}
                          disableScroolIntoView
                          suffix={` ${feature.unitLabel}`}
                          typeInput={'controlled-inline-error'}
                          noErrorMessage
                          withoutAsterisk
                          rules={{
                            required: true,
                            validate: (value) => {
                              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                              if (feature.limitMaxValue && transformValue > feature.limitMaxValue) {
                                return `Der bei ${feature.label} eingegebene Wert liegt oberhalb des Maximalwerts von ${formatNumber(feature.limitMaxValue, 1)} ${feature.unitLabel} Bitte korrigieren Sie Ihre Eingabe.`
                              }

                              if (feature.limitMinValue && transformValue < feature.limitMinValue) {
                                return `Der bei ${feature.label} eingegebene Wert liegt unten des Minimal von ${formatNumber(feature.limitMinValue, 1)} ${feature.unitLabel} Bitte korrigieren Sie Ihre Eingabe.`
                              }

                              if (isTotalSumInvalid) {
                                setError(`additionalQuestions.landscapeFeatureTypes[${fieldIndex}].value`, { message: '' })
                              }

                              return true
                            }
                          }}
                          name={`additionalQuestions.landscapeFeatureTypes[${fieldIndex}].value`}
                          placeholder={`in ${feature.unitLabel}`}
                        />
                      ) : (
                        <Input
                          style={{ width: 200 }}
                          disabled
                          placeholder={`in ${feature.unitLabel}`}
                        />
                      )}
                    </Grid.Column>
                  </Grid.Row>
                )
              })}
            </Grid>
          </Form.Field>
        </StyledGroup>
      )}

      <StyledGroup widths={'equal'}>
        <Controller
          control={control}
          name='additionalQuestions.otherAreasWithSpecialEcologicalValuePresent'
          rules={{ required: true }}
          render={({ fieldState: { error } }) => (
            <Form.Field error={Boolean(error)}>
              <label className='title' style={{ width: 'fit-content' }}>
                <div>{'Gibt es weitere Flächen mit besonderem ökologischem Wert?'}</div>
                <StyledDivider />
              </label>
              <div style={{ display: 'flex', gap: 20 }}>
                <Checkbox
                  radio
                  label={'Vorhanden'}
                  checked={watchOtherAreasPresent === 1}
                  onClick={() => {
                    setValue('additionalQuestions.otherAreasWithSpecialEcologicalValuePresent', 1)
                    clearErrors('additionalQuestions.otherAreasWithSpecialEcologicalValuePresent')
                  }}
                  />
                <Checkbox
                  radio
                  label={'Nicht vorhanden'}
                  checked={watchOtherAreasPresent === 0}
                  onClick={() => {
                    setValue('additionalQuestions.otherAreasWithSpecialEcologicalValuePresent', 0)
                    setValue('additionalQuestions.specialEcologicalAreas', [])
                    clearErrors('additionalQuestions.otherAreasWithSpecialEcologicalValuePresent')
                  }}
                  />
              </div>
            </Form.Field>
          )}
          />
      </StyledGroup>

      {Boolean(watchOtherAreasPresent) && (
        <StyledGroup widths={'equal'}>
          <Form.Field>
            <label className='title' style={{ width: 'fit-content' }}>
              <div>{'Wie viele Hektar landwirtschaftlich genutzter Flächen mit besonderem ökologischen Wert bewirtschaften Sie insgesamt?'}</div>
              <StyledDivider />
            </label>
            <Grid columns={2} style={{ marginTop: '1em' }}>
              {specialEcologicalAreas.map(area => {
                const fieldIndex = ecologicalFields.findIndex(f => f['id'] === area.id)
                const isChecked = fieldIndex >= 0
                const fieldValue = isChecked ? ecologicalFields[fieldIndex]['value'] : ''
                const displayLabel = isChecked && fieldValue
                  ? `${area.label} (${fieldValue} ${area.unitLabel})`
                  : area.label

                return (
                  <Grid.Row key={area.id} style={{ alignItems: 'center', padding: '0.6rem 0rem' }}>
                    <Grid.Column width={8}>
                      <Checkbox
                        label={displayLabel}
                        checked={isChecked}
                        onChange={(_, data) => handleEcologicalChecked(area, data.checked)}
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      {isChecked ? (
                        <FormFields.Input
                          control={control}
                          style={{ width: 200 }}
                          styleLabel={{ marginBottom: 0 }}
                          allowDecimals
                          disableScroolIntoView
                          decimalScale={1}
                          typeInput={'controlled-inline-error'}
                          suffix={` ${area.unitLabel}`}
                          noErrorMessage
                          withoutAsterisk
                          rules={{
                            required: true,
                            validate: (value) => {
                              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                              if (area.limitMaxValue && transformValue > area.limitMaxValue) {
                                return `Der bei ${area.label} eingegebene Wert liegt oberhalb des Maximalwerts von ${formatNumber(area.limitMaxValue, 1)} ${area.unitLabel} Bitte korrigieren Sie Ihre Eingabe.`
                              }

                              if (area.limitMinValue && transformValue < area.limitMinValue) {
                                return `Der bei ${area.label} eingegebene Wert liegt unten des Minimal von ${formatNumber(area.limitMinValue, 1)} ${area.unitLabel} Bitte korrigieren Sie Ihre Eingabe.`
                              }

                              if (isTotalSumInvalid) {
                                setError(`additionalQuestions.specialEcologicalAreas[${fieldIndex}].value`, { message: '' })
                              }

                              return true
                            }
                          }}
                          name={`additionalQuestions.specialEcologicalAreas[${fieldIndex}].value`}
                          placeholder={`in ${area.unitLabel}`}
                        />
                      ) : (
                        <Input
                          style={{ width: 200 }}
                          disabled
                          placeholder={`in ${area.unitLabel}`}
                        />
                      )}
                    </Grid.Column>
                  </Grid.Row>
                )
              })}
            </Grid>
          </Form.Field>
        </StyledGroup>
      )}

      {hasAnyValues && (
        <StyledGroup widths={'equal'}>
          <Form.Field>
            <div style={{ marginTop: '0.5em' }}>
              <span style={{ fontWeight: 'bold', color: isTotalSumInvalid ? '#9f3a38' : undefined }}>
                {`Aktuelle Gesamtsumme (Landschaftselemente + Ökologische Flächen): ${formatNumber(totalSum, 1)}`}
              </span>
              {isTotalSumInvalid && (
                <Message negative size='small' style={{ marginTop: '0.5em' }}>
                  {`Die Gesamtsumme beträgt ${formatNumber(totalSum, 1)} und liegt außerhalb des zulässigen Bereichs von ${formatNumber(VALID_SUM_MIN, 0)} bis ${formatNumber(VALID_SUM_MAX, 0)}.`}
                </Message>
              )}
            </div>
          </Form.Field>
        </StyledGroup>
      )}
      
    </div>
  )
}

export default OekologieStep
